import * as THREE from "three";
import {
  createDropLabel,
  createGradientCircle,
  createLabel,
  createLine,
  getModelSize,
  handleCircleSize,
} from "./threeJsUtils";
import { initialLineWidth } from "../Components/Settings/defaultSettings";

/**
 * Set model position, rotation, and scale based on building default view
 */
export const setupModelTransform = (model, floorLinesGroup, building) => {
  if (building.default_view) {
    if (building.default_view.position) {
      model.position.set(
        building.default_view.position.x,
        0,
        building.default_view.position.z
      );
      floorLinesGroup.position.set(
        building.default_view.position.x,
        0,
        building.default_view.position.z
      );
    }

    if (building.default_view.rotation) {
      model.rotation.set(0, building.default_view.rotation._y, 0);
    }
    if(floorLinesGroup && building.default_view.floorsLinesRotationY ) {
      floorLinesGroup.rotation.set(
        0,
        building.default_view.floorsLinesRotationY
          ? building.default_view.floorsLinesRotationY
          : 0,
        0
      );

    }

    if (building.default_view.scale) {
      model.scale.set(
        building.default_view.scale.x,
        building.default_view.scale.x,
        building.default_view.scale.x
      );
      floorLinesGroup.scale.set(
        building.default_view.scale.x,
        building.default_view.scale.x,
        building.default_view.scale.x
      );
    }
  } else {
    model.position.set(0, 0, 0);
    floorLinesGroup.position.set(0, 0, 0);
  }
};

/**
 * Handle floor nodes - create floor lines and labels
 */
export const handleFloorNode = (
  node,
  model,
  building,
  projectDefaultView,
  scene,
  floorLinesGroup,
  floorsNodes,
  floorLines,
  floorLabels
) => {
  node.userData["buildingName"] = building.name;
  floorsNodes.current.push(node);

  const floorLabel = createLabel(node.name.split("_")[1]);
  let endPos,
    startPos = {
      x: model.position.x,
      y: node.position.y,
      z: node.position.z,
    };
  let lineWidth = projectDefaultView.lineWidth
    ? +projectDefaultView.lineWidth
    : initialLineWidth;

  if (model.position.x > 0) {
    endPos = {
      x: model.position.x + lineWidth,
      y: node.position.y,
      z: model.position.z,
    };
  } else {
    endPos = {
      x: model.position.x - lineWidth,
      y: node.position.y,
      z: model.position.z,
    };
  }

  const line = createLine(
    startPos,
    endPos,
    projectDefaultView.floorLineColor
      ? projectDefaultView.floorLineColor
      : "white"
  );
  scene.current.add(line);
  line.userData.startPos = startPos;
  line.userData.endPos = endPos;
  floorLabel.position.set(endPos.x, endPos.y, endPos.z);
  line.label = floorLabel;
  floorLines.current.push(line);
  floorLabels.current.push(floorLabel);
  floorLabel.scale.set(10, 10, 1);
  floorLinesGroup.add(line);
  floorLinesGroup.add(floorLabel);
  floorLinesGroup.visible = building.show_floors_lines;
};

/**
 * Handle apartment type nodes (bbox with balcony)
 */
export const handleApartmentTypeNode = (
  node,
  building,
  scene,
  appTypeModelsRef
) => {
  const balconyNode = node.children.find((child) =>
    child.name.includes("balcony")
  );
  if (balconyNode && node.userData.apartment && node.userData.apartment.apartment_type) {
    const appType = node.userData.apartment.apartment_type;

    if (!appTypeModelsRef.current[appType.name]) {
      const clonedNode = node.clone();
      clonedNode.material.opacity = 0;
      clonedNode.visible = false;
      if (building.default_view.scale) {
        clonedNode.scale.set(
          building.default_view.scale.x,
          building.default_view.scale.y,
          building.default_view.scale.z
        );
      }
      scene.current.add(clonedNode);
      appTypeModelsRef.current[appType.name] = clonedNode;
    }
  }
};



/**
 * Handle area nodes - create area labels
 */
export const handleAreaNode = (node, scene, areaNodesRef, occupiedHoveredColor) => {
  node.visible = false;
  areaNodesRef.current.push(node);
  node.material = occupiedHoveredColor;
  const label = createDropLabel(
    node.name.split("_").join(" "),
    "black",
    "white"
  );
  scene.current.add(label);
  label.position.set(node.position.x, node.position.y + 20, node.position.z);
};

/**
 * Handle label nodes - create sprite labels
 */
export const handleLabelNode = (node, scene, spritesNodesRef) => {
  node.visible = false;
  const nodePosition = node.position;
  const nodeLabel = createDropLabel(node.name.split("_")[1], "black", "white");
  scene.current.add(nodeLabel);
  nodeLabel.position.set(nodePosition.x, nodePosition.y + 30, nodePosition.z);
  spritesNodesRef.current.push(nodeLabel);
  scene.current.add(nodeLabel);
};

/**
 * Create building circle and label
 */
export const createBuildingCircleAndLabel = (
  model,
  building,
  scene,
  gradientColors
) => {
  const circleRadius = 30;

  // Create gradient circle
  const circle = createGradientCircle(
    circleRadius,
    building.default_view && building.default_view.position
      ? building.default_view.position.x
      : 0,
    building.default_view && building.default_view.position
      ? building.default_view.position.y
      : 0,
    building.default_view && building.default_view.position
      ? building.default_view.position.z
      : 0,
    gradientColors,
    false
  );
  scene.current.add(circle);

  if (building.default_view && building.default_view.circleRadiusOffset) {
    handleCircleSize(building.default_view.circleRadiusOffset, circle);
  }

  console.log("CREATE BUILDING LABEL", building.name);
  const buildingLabel = createDropLabel(building.name, "black", "white");
  buildingLabel.userData = { id: building.id };

  const modelSize = getModelSize(model);
  buildingLabel.position.set(
    building.default_view && building.default_view.position
      ? building.default_view.position.x
      : 0,
    modelSize.y * building.default_view.scale.y + 1,
    building.default_view && building.default_view.position
      ? building.default_view.position.z
      : 0
  );
  scene.current.add(buildingLabel);

  return { circle, buildingLabel };
};

/**
 * Process all nodes in the building model
 */
export const processModelNodes = (
  model,
  building,
  projectDefaultView,
  scene,
  floorLinesGroup,
  refs,
  materials
) => {
  const {
    floorsNodes,
    floorLines,
    floorLabels,
    appTypeModelsRef,
    areaNodesRef,
    spritesNodesRef,
  } = refs;
  
  const { vacantColor, occupiedColor, occupiedHoveredColor } = materials;

  model.traverse(function (node) {
    if (node.isMesh) {
      // node.castShadow = true;
    }

    // Handle floor nodes
    if (node.name.includes("Floor") && node.name.includes("base")) {
      handleFloorNode(
        node,
        model,
        building,
        projectDefaultView,
        scene,
        floorLinesGroup,
        floorsNodes,
        floorLines,
        floorLabels
      );
    }

    // Handle apartment type nodes (bbox with balcony)
    if (node.name.includes("bbox")) {
      handleApartmentTypeNode(node, building, scene, appTypeModelsRef);

    }

    // Handle area nodes
    if (node.name.includes("area")) {
      handleAreaNode(node, scene, areaNodesRef, occupiedHoveredColor);
    }

    // Handle label nodes
    if (node.name.includes("label")) {
      handleLabelNode(node, scene, spritesNodesRef);
    }
  });
};
